/**
 * Cookie-consent state, shared across every Booqly frontend.
 *
 * The flag lives in a first-party cookie scoped to the shared parent domain
 * (`.booqly.co` in prod, `.dev.booqly.co` in dev, host-only on localhost — which
 * is already shared across dev ports since cookies aren't port-scoped). That's
 * why a choice made on one Booqly app suppresses the banner on all the others;
 * localStorage couldn't do this because it's per-origin.
 *
 * Not httpOnly on purpose — the banner reads it from JS to decide whether to show.
 */
export type Consent = "all" | "essential";

const COOKIE_NAME = "booqly_cookie_consent";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 180; // 180 days

const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN ?? "";

/** Landing site's privacy policy — absolute so the link works from any subdomain. */
export const PRIVACY_URL = `${(
  import.meta.env.VITE_LANDING_URL ?? "https://booqly.co"
).replace(/\/$/, "")}/privacy`;

export function getConsent(): Consent | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  const value = match?.slice(match.indexOf("=") + 1);
  return value === "all" || value === "essential" ? value : null;
}

export function setConsent(value: Consent): void {
  if (typeof document === "undefined") return;
  const parts = [
    `${COOKIE_NAME}=${value}`,
    "Path=/",
    `Max-Age=${MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ];
  if (COOKIE_DOMAIN) parts.push(`Domain=${COOKIE_DOMAIN}`);
  if (window.location.protocol === "https:") parts.push("Secure");
  document.cookie = parts.join("; ");
}
